import  { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Loader2,
  DollarSign,
  CreditCard,
  Receipt,
  Calculator,
} from "lucide-react";
import { 
  type ShiftSummary, 
  type CashClosingReport, 
  getShiftSummary, 
  submitShiftReport 
} from "@/services";
import { useAuthStore } from "@/stores/authStore";
import { useToast } from "@/hooks/useToast";

interface CashClosingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

/**
 * Modal de cierre de caja optimizado para dispositivos móviles
 *
 * Permite al cajero declarar el efectivo contado y generar
 * un reporte de cierre de turno con cálculos automáticos.
 */
export default function CashClosingModal({
  isOpen,
  onClose,
  onLogout,
}: CashClosingModalProps) {
  const [shiftSummary, setShiftSummary] = useState<ShiftSummary | null>(null);
  const [declaredCash, setDeclaredCash] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuthStore();
  const { toast } = useToast();

  // Calcular diferencia entre efectivo del sistema y declarado
  const difference = shiftSummary ? declaredCash - shiftSummary.cashSales : 0;
  const isDifferenceZero = difference === 0;
  // const hasDifference = difference !== 0;

  // Cargar resumen del turno al abrir el modal
  useEffect(() => {
    if (isOpen && !shiftSummary) {
      loadShiftSummary();
    }
  }, [isOpen, shiftSummary]);

  const loadShiftSummary = async () => {
    setIsLoading(true);
    try {
      const summary = await getShiftSummary();
      setShiftSummary(summary);
      // Pre-llenar con el efectivo del sistema
      setDeclaredCash(summary.cashSales);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo cargar el resumen del turno",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!shiftSummary || !user) return;

    setIsSubmitting(true);
    try {
      const reportData: CashClosingReport = {
        shiftSummary,
        declaredCash,
        difference,
        timestamp: new Date().toISOString(),
        userId: user.id,
      };

      await submitShiftReport(reportData);

      toast({
        title: "Cierre de Caja Exitoso",
        description: "El reporte de cierre de turno ha sido enviado",
      });

      onClose();
      onLogout();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo procesar el cierre de caja",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="h-5 w-5" />
            Cierre de Caja
          </DialogTitle>
          <DialogDescription>
            Declara el efectivo contado y confirma el cierre del turno
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Resumen del Sistema */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Receipt className="h-4 w-4" />
                Resumen del Turno
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">
                    Cargando resumen...
                  </span>
                </div>
              ) : shiftSummary ? (
                <div className="space-y-4">
                  {/* Información del turno */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div>
                      <span className="font-medium">Inicio del turno:</span>{" "}
                      {formatDate(shiftSummary.startTime)}
                    </div>
                    <div>
                      <span className="font-medium">Fin del turno:</span>{" "}
                      {formatDate(shiftSummary.endTime)}
                    </div>
                  </div>

                  {/* Tabla de resumen */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-sm">Concepto</TableHead>
                          <TableHead className="text-right text-sm">
                            Monto
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="flex items-center gap-2 text-sm">
                            <DollarSign className="h-4 w-4 text-primary" />
                            Ventas en Efectivo
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            {formatCurrency(shiftSummary.cashSales)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="flex items-center gap-2 text-sm">
                            <CreditCard className="h-4 w-4 text-secondary" />
                            Ventas con Tarjeta
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            {formatCurrency(shiftSummary.cardSales)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="flex items-center gap-2 text-sm">
                            <Receipt className="h-4 w-4 text-accent" />
                            Ventas a Crédito
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            {formatCurrency(shiftSummary.creditSales)}
                          </TableCell>
                        </TableRow>
                        <TableRow className="border-t-2 border-border">
                          <TableCell className="font-bold text-base">
                            Total Ventas
                          </TableCell>
                          <TableCell className="text-right font-bold text-base">
                            {formatCurrency(shiftSummary.totalSales)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-sm">
                            Total Transacciones
                          </TableCell>
                          <TableCell className="text-right font-medium text-sm">
                            {shiftSummary.transactions}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No se pudo cargar el resumen del turno
                </div>
              )}
            </CardContent>
          </Card>

          {/* Declaración del Cajero */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Declaración del Cajero</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="declaredCash" className="text-sm font-medium">
                  Monto contado en caja (efectivo)
                </Label>
                <Input
                  id="declaredCash"
                  type="number"
                  value={declaredCash}
                  onChange={(e) => setDeclaredCash(Number(e.target.value) || 0)}
                  placeholder="Ingresa el monto contado"
                  className="h-12 text-lg font-medium"
                />
              </div>

              {/* Cálculo de Diferencia */}
              {shiftSummary && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Diferencia</Label>
                  <div
                    className={`p-4 rounded-lg border-2 ${
                      isDifferenceZero
                        ? "border-primary/20 bg-primary/5 text-primary"
                        : "border-destructive/20 bg-destructive/5 text-destructive"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">
                        {isDifferenceZero
                          ? "✓ Cuadra perfectamente"
                          : "⚠ Hay diferencia"}
                      </span>
                      <span
                        className={`text-lg font-bold ${
                          isDifferenceZero ? "text-primary" : "text-destructive"
                        }`}
                      >
                        {formatCurrency(Math.abs(difference))}
                        {difference > 0
                          ? " (Sobrante)"
                          : difference < 0
                          ? " (Faltante)"
                          : ""}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="gap-3 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="h-12 px-6 text-base"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !shiftSummary}
            className="min-w-[160px] h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Procesando...
              </>
            ) : (
              "Confirmar y Cerrar Turno"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
